import React, { useState } from 'react';
import {
  Receipt, Search, Filter, Download, Eye, Calendar, DollarSign,
  CheckCircle, Clock, AlertCircle, X, FileText, Printer, Send,
  Plus, Edit2, Percent, Package, Weight, Calculator, ChevronDown,
  ChevronUp, CreditCard, Trash2
} from 'lucide-react';

// Pricing tiers configuration
const pricingTiers = [
  { id: 'tier1', name: 'Tier 1 (0-10 lbs)', minWeight: 0, maxWeight: 10, pricePerLb: 150, color: 'lime' },
  { id: 'tier2', name: 'Tier 2 (10-25 lbs)', minWeight: 10, maxWeight: 25, pricePerLb: 130, color: 'cyan' },
  { id: 'tier3', name: 'Tier 3 (25-50 lbs)', minWeight: 25, maxWeight: 50, pricePerLb: 110, color: 'blue' },
  { id: 'tier4', name: 'Tier 4 (50+ lbs)', minWeight: 50, maxWeight: 999, pricePerLb: 95, color: 'purple' },
];

// Mock invoices with tier-based pricing
const mockInvoices = [
  {
    id: 'INV-FS-8521',
    date: 'Dec 15, 2025',
    dueDate: 'Dec 30, 2025',
    bom_id: 'BOM-2025-12-15',
    customer_name: 'Marcus Johnson',
    status: 'pending',
    items: [
      { id: 'item-1', description: 'Package TRK-45821', weight: 12.5, tier: 'tier2', rate: 130, amount: 1625 },
      { id: 'item-2', description: 'Package TRK-45820', weight: 8.2, tier: 'tier1', rate: 150, amount: 1230 },
      { id: 'item-3', description: 'Package TRK-45819', weight: 22.1, tier: 'tier2', rate: 130, amount: 2873 },
    ],
    subtotal: 5728,
    discount_type: 'percentage',
    discount_value: 10,
    discount_amount: 572.80,
    tax_rate: 0,
    tax_amount: 0,
    total: 5155.20,
    notes: 'Loyalty discount applied'
  },
  {
    id: 'INV-FS-8498',
    date: 'Dec 10, 2025',
    dueDate: 'Dec 25, 2025',
    bom_id: 'BOM-2025-12-14',
    customer_name: 'Sarah Williams',
    status: 'pending',
    items: [
      { id: 'item-4', description: 'Package TRK-45817', weight: 5.8, tier: 'tier1', rate: 150, amount: 870 },
      { id: 'item-5', description: 'Package TRK-45816', weight: 32.4, tier: 'tier3', rate: 110, amount: 3564 },
    ],
    subtotal: 4434,
    discount_type: null,
    discount_value: 0,
    discount_amount: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 4434,
    notes: ''
  },
  {
    id: 'INV-FS-8476',
    date: 'Dec 5, 2025',
    dueDate: 'Dec 20, 2025',
    bom_id: 'BOM-2025-12-13',
    customer_name: 'James Brown',
    status: 'overdue',
    items: [
      { id: 'item-6', description: 'Package TRK-45815', weight: 55.2, tier: 'tier4', rate: 95, amount: 5244 },
    ],
    subtotal: 5244,
    discount_type: 'fixed',
    discount_value: 500,
    discount_amount: 500,
    tax_rate: 0,
    tax_amount: 0,
    total: 4744,
    notes: 'Volume discount - fixed amount'
  },
  {
    id: 'INV-FS-8454',
    date: 'Dec 1, 2025',
    dueDate: 'Dec 15, 2025',
    paidDate: 'Dec 12, 2025',
    bom_id: 'BOM-2025-12-12',
    customer_name: 'Emily Davis',
    status: 'paid',
    items: [
      { id: 'item-7', description: 'Package TRK-45814', weight: 18.3, tier: 'tier2', rate: 130, amount: 2379 },
      { id: 'item-8', description: 'Package TRK-45813', weight: 7.5, tier: 'tier1', rate: 150, amount: 1125 },
    ],
    subtotal: 3504,
    discount_type: null,
    discount_value: 0,
    discount_amount: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 3504,
    notes: ''
  },
];

const summaryData = {
  pending: { count: 2, amount: 9589.20 },
  overdue: { count: 1, amount: 4744 },
  paid: { count: 1, amount: 3504 },
  total: { count: 4, amount: 17837.20 },
};

export default function TenantInvoiceSystem() {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  const filteredInvoices = mockInvoices.filter(inv => {
    const matchesFilter = filter === 'all' || inv.status === filter;
    const matchesSearch = 
      inv.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle size={14} />;
      case 'pending': return <Clock size={14} />;
      case 'overdue': return <AlertCircle size={14} />;
      default: return null;
    }
  };

  const getTierBadge = (tierId) => {
    const tier = pricingTiers.find(t => t.id === tierId);
    return tier ? (
      <span className={`tier-badge ${tier.color}`}>{tier.name.split(' ')[0]} {tier.name.split(' ')[1]}</span>
    ) : null;
  };

  const openDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const toggleExpand = (invoiceId) => {
    setExpandedInvoice(expandedInvoice === invoiceId ? null : invoiceId);
  };

  return (
    <div className="tenant-invoice-system" data-testid="tenant-invoice-system">
      {/* Summary Cards */}
      <div className="invoice-summary-grid">
        <div className="summary-card total" data-testid="summary-total">
          <div className="summary-icon">
            <Receipt size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-value">JMD {summaryData.total.amount.toLocaleString()}</span>
            <span className="summary-label">Total ({summaryData.total.count} invoices)</span>
          </div>
        </div>
        
        <div className="summary-card amber" data-testid="summary-pending">
          <div className="summary-icon">
            <Clock size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-value">JMD {summaryData.pending.amount.toLocaleString()}</span>
            <span className="summary-label">Pending ({summaryData.pending.count})</span>
          </div>
        </div>
        
        <div className="summary-card rose" data-testid="summary-overdue">
          <div className="summary-icon">
            <AlertCircle size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-value">JMD {summaryData.overdue.amount.toLocaleString()}</span>
            <span className="summary-label">Overdue ({summaryData.overdue.count})</span>
          </div>
        </div>
        
        <div className="summary-card lime" data-testid="summary-paid">
          <div className="summary-icon">
            <CheckCircle size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-value">JMD {summaryData.paid.amount.toLocaleString()}</span>
            <span className="summary-label">Paid ({summaryData.paid.count})</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="invoice-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search invoice #, customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="invoice-search"
            />
          </div>
          
          <div className="filter-buttons">
            {['all', 'pending', 'overdue', 'paid'].map((f) => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        
        <div className="toolbar-right">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowPricingModal(true)}
            data-testid="pricing-tiers-btn"
          >
            <Calculator size={14} /> Pricing Tiers
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-invoice-btn"
          >
            <Plus size={14} /> Create Invoice
          </button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="invoices-list expandable">
        {filteredInvoices.map((invoice) => (
          <div 
            key={invoice.id} 
            className={`invoice-card ${invoice.status} ${expandedInvoice === invoice.id ? 'expanded' : ''}`}
            data-testid={`invoice-${invoice.id}`}
          >
            <div className="invoice-card-main" onClick={() => toggleExpand(invoice.id)}>
              <div className="invoice-id-section">
                <Receipt size={20} />
                <div>
                  <span className="invoice-id">{invoice.id}</span>
                  <span className="invoice-customer">{invoice.customer_name}</span>
                </div>
              </div>
              
              <div className="invoice-dates">
                <div className="date-item">
                  <Calendar size={14} />
                  <span>Issued: {invoice.date}</span>
                </div>
                <div className="date-item">
                  <Clock size={14} />
                  <span>Due: {invoice.dueDate}</span>
                </div>
              </div>

              <div className="invoice-bom">
                <FileText size={14} />
                <span>{invoice.bom_id}</span>
              </div>

              <div className="invoice-amount-section">
                <span className="invoice-amount">JMD {invoice.total.toLocaleString()}</span>
                {invoice.discount_amount > 0 && (
                  <span className="discount-badge">
                    <Percent size={10} /> {invoice.discount_type === 'percentage' ? `${invoice.discount_value}%` : `JMD ${invoice.discount_value}`} off
                  </span>
                )}
                <span className={`status-badge ${invoice.status}`}>
                  {getStatusIcon(invoice.status)}
                  {invoice.status}
                </span>
              </div>

              <button className="expand-btn">
                {expandedInvoice === invoice.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {/* Expanded Content */}
            {expandedInvoice === invoice.id && (
              <div className="invoice-card-expanded">
                <div className="line-items-section">
                  <h4>Line Items</h4>
                  <div className="line-items-table">
                    <div className="table-header">
                      <span>Description</span>
                      <span>Weight</span>
                      <span>Tier</span>
                      <span>Rate/lb</span>
                      <span>Amount</span>
                    </div>
                    {invoice.items.map((item) => (
                      <div key={item.id} className="table-row">
                        <span>{item.description}</span>
                        <span>{item.weight} lbs</span>
                        <span>{getTierBadge(item.tier)}</span>
                        <span>JMD {item.rate}</span>
                        <span>JMD {item.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="invoice-totals">
                  <div className="total-row">
                    <span>Subtotal</span>
                    <span>JMD {invoice.subtotal.toLocaleString()}</span>
                  </div>
                  {invoice.discount_amount > 0 && (
                    <div className="total-row discount">
                      <span>
                        Discount ({invoice.discount_type === 'percentage' ? `${invoice.discount_value}%` : 'Fixed'})
                      </span>
                      <span>- JMD {invoice.discount_amount.toLocaleString()}</span>
                    </div>
                  )}
                  {invoice.tax_amount > 0 && (
                    <div className="total-row">
                      <span>Tax ({invoice.tax_rate}%)</span>
                      <span>JMD {invoice.tax_amount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="total-row final">
                    <span>Total</span>
                    <span>JMD {invoice.total.toLocaleString()}</span>
                  </div>
                </div>

                <div className="invoice-card-actions">
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => { e.stopPropagation(); openDetail(invoice); }}
                    data-testid={`view-${invoice.id}`}
                  >
                    <Eye size={14} /> Full Details
                  </button>
                  <button className="btn btn-ghost btn-sm" data-testid={`download-${invoice.id}`}>
                    <Download size={14} /> PDF
                  </button>
                  <button className="btn btn-ghost btn-sm" data-testid={`send-${invoice.id}`}>
                    <Send size={14} /> Send
                  </button>
                  {invoice.status !== 'paid' && (
                    <button className="btn btn-primary btn-sm" data-testid={`pay-${invoice.id}`}>
                      <CreditCard size={14} /> Record Payment
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pricing Tiers Modal */}
      {showPricingModal && (
        <div className="modal-overlay" onClick={() => setShowPricingModal(false)}>
          <div 
            className="modal-content pricing-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="pricing-modal"
          >
            <div className="modal-header">
              <h3>Pricing Tiers</h3>
              <button className="close-btn" onClick={() => setShowPricingModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="pricing-description">
                Freight charges are calculated based on package weight using tiered pricing.
              </p>
              
              <div className="pricing-tiers-grid">
                {pricingTiers.map((tier) => (
                  <div key={tier.id} className={`tier-card ${tier.color}`} data-testid={`tier-${tier.id}`}>
                    <div className="tier-header">
                      <span className="tier-name">{tier.name}</span>
                    </div>
                    <div className="tier-rate">
                      <span className="rate-value">JMD {tier.pricePerLb}</span>
                      <span className="rate-unit">per lb</span>
                    </div>
                    <div className="tier-range">
                      <Weight size={14} />
                      <span>{tier.minWeight} - {tier.maxWeight === 999 ? '∞' : tier.maxWeight} lbs</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pricing-example">
                <h4>Example Calculation</h4>
                <div className="example-box">
                  <div className="example-row">
                    <span>Package Weight</span>
                    <span>15.5 lbs</span>
                  </div>
                  <div className="example-row">
                    <span>Applicable Tier</span>
                    <span className="tier-badge cyan">Tier 2</span>
                  </div>
                  <div className="example-row">
                    <span>Rate</span>
                    <span>JMD 130/lb</span>
                  </div>
                  <div className="example-row total">
                    <span>Freight Charge</span>
                    <span>JMD 2,015</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPricingModal(false)}>
                Close
              </button>
              <button className="btn btn-primary" data-testid="edit-pricing">
                <Edit2 size={14} /> Edit Tiers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div 
            className="modal-content create-invoice-modal large"
            onClick={(e) => e.stopPropagation()}
            data-testid="create-invoice-modal"
          >
            <div className="modal-header">
              <h3>Create New Invoice</h3>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Customer</label>
                  <select data-testid="select-customer">
                    <option value="">Select customer...</option>
                    <option value="cust-001">Marcus Johnson</option>
                    <option value="cust-002">Sarah Williams</option>
                    <option value="cust-003">James Brown</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Link to BOM</label>
                  <select data-testid="select-bom">
                    <option value="">Select BOM...</option>
                    <option value="BOM-2025-12-15">BOM-2025-12-15 (47 packages)</option>
                    <option value="BOM-2025-12-14">BOM-2025-12-14 (82 packages)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" data-testid="due-date" />
                </div>
                <div className="form-group">
                  <label>Discount Type</label>
                  <select data-testid="discount-type">
                    <option value="">No discount</option>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (JMD)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Discount Value</label>
                  <input type="number" placeholder="0" data-testid="discount-value" />
                </div>
              </div>

              <div className="auto-generate-section">
                <div className="info-box">
                  <Calculator size={16} />
                  <span>Line items will be auto-generated from selected BOM packages using tier-based pricing</span>
                </div>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea 
                  placeholder="Add invoice notes..."
                  rows={3}
                  data-testid="invoice-notes"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="generate-invoice">
                <Calculator size={14} /> Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal-content invoice-detail-modal large"
            onClick={(e) => e.stopPropagation()}
            data-testid="invoice-detail-modal"
          >
            <div className="modal-header">
              <div>
                <h3>Invoice Details</h3>
                <span className="modal-subtitle">{selectedInvoice.id}</span>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="invoice-header-section">
                <div className="invoice-from">
                  <h4>From</h4>
                  <p className="company-name">FastShip Jamaica</p>
                  <p className="dim">Kingston, Jamaica</p>
                </div>
                <div className="invoice-to">
                  <h4>Bill To</h4>
                  <p className="customer-name">{selectedInvoice.customer_name}</p>
                </div>
                <div className="invoice-meta">
                  <div className="meta-row">
                    <span>Invoice #</span>
                    <span>{selectedInvoice.id}</span>
                  </div>
                  <div className="meta-row">
                    <span>Issue Date</span>
                    <span>{selectedInvoice.date}</span>
                  </div>
                  <div className="meta-row">
                    <span>Due Date</span>
                    <span>{selectedInvoice.dueDate}</span>
                  </div>
                  <div className="meta-row">
                    <span>BOM Reference</span>
                    <span>{selectedInvoice.bom_id}</span>
                  </div>
                  <div className="meta-row">
                    <span>Status</span>
                    <span className={`status-badge ${selectedInvoice.status}`}>
                      {selectedInvoice.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="invoice-line-items">
                <div className="line-item-header">
                  <span>Description</span>
                  <span>Weight</span>
                  <span>Tier</span>
                  <span>Rate</span>
                  <span>Amount</span>
                </div>
                {selectedInvoice.items.map((item) => (
                  <div key={item.id} className="line-item">
                    <span>{item.description}</span>
                    <span>{item.weight} lbs</span>
                    <span>{getTierBadge(item.tier)}</span>
                    <span>JMD {item.rate}/lb</span>
                    <span>JMD {item.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="invoice-totals-section">
                <div className="totals-box">
                  <div className="total-row">
                    <span>Subtotal</span>
                    <span>JMD {selectedInvoice.subtotal.toLocaleString()}</span>
                  </div>
                  {selectedInvoice.discount_amount > 0 && (
                    <div className="total-row discount">
                      <span>
                        Discount ({selectedInvoice.discount_type === 'percentage' 
                          ? `${selectedInvoice.discount_value}%` 
                          : `Fixed JMD ${selectedInvoice.discount_value}`})
                      </span>
                      <span className="negative">- JMD {selectedInvoice.discount_amount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="total-row grand-total">
                    <span>Total Due</span>
                    <span>JMD {selectedInvoice.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="invoice-notes">
                  <h4>Notes</h4>
                  <p>{selectedInvoice.notes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" data-testid="print-invoice">
                <Printer size={14} /> Print
              </button>
              <button className="btn btn-secondary" data-testid="download-invoice">
                <Download size={14} /> Download PDF
              </button>
              <button className="btn btn-secondary" data-testid="send-invoice">
                <Send size={14} /> Send to Customer
              </button>
              {selectedInvoice.status !== 'paid' && (
                <button className="btn btn-primary" data-testid="record-payment">
                  <CreditCard size={14} /> Record Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
